import React, { useLayoutEffect, useRef } from 'react';
import {
  useAsyncValue,
  useNavigate,
  useSearchParams,
  useSubmit,
} from 'react-router-dom';

// Wizard reusado en modo edición (F4 2026-07-17)
import NewRoutePage, {
  type EditRouteInitialValues,
  type EditRouteOperatorsSeed,
  type EditRouteSubmitPayload,
} from '../NewRoute';

// Types
import { SaveSurveillanceRouteInput } from '../../../../types/surveillanceRoute';
import { type EditRouteDataToCompare } from './loader';

// Constants / utils
import { formNaming as formSurveillanceRouteNaming } from '../../../../components/forms/SurveillanceRouteForm/constants';
import {
  compressSessionStorage,
} from '../../../../utils/sessionStorage';
import { parsePk } from '../../../../utils/transform';
import { showErrorToast } from '../../../../utils/toasts';
import i18n from '../../../../i18n';
import { canEditSurveillanceRoute } from '../../../../utils/surveillanceRoute';
import { useAuth } from '../../../../hooks/useAuth';
import { useSurveillanceRouteOwnership } from '../../../../hooks/useSurveillanceRouteOwnership';

// ═══════════════════════════════════════════════════════════════════
// PÁGINA EditRoute (F4 2026-07-17)
//
// Espejo estructural de EditCampaignPage: monta el wizard de NewRoute en modo
// edición (`editMode`) con los valores precargados del loader, y hace el
// GUARDADO por diff — `difference` semántico contra `dataToCompare` + guard
// "sin cambios no dispara" (patrón EditCampaign) — antes de hacer submit a la
// action del router. Trampa entidad doble (spec §6): `input.id` es el id de la
// ENTIDAD SurveillanceRoute (routeId de la URL), NUNCA el de la Order gemela.
// ═══════════════════════════════════════════════════════════════════

interface EditRouteLoaderData {
  routeId: string;
  editInitialValues: EditRouteInitialValues | null;
  editOperatorsSeed: EditRouteOperatorsSeed;
  dataToCompare: EditRouteDataToCompare | null;
  hasSegments: boolean;
}

/** Key del borrador de Operarios del wizard de edición (no pisa el de creación). */
const EDIT_OPERATORS_KEY =
  formSurveillanceRouteNaming.EDIT_SURVEILLANCE_ROUTE_OPERATORS_FORM;

export default function EditRoutePage(): JSX.Element | null {
  const loaderData = useAsyncValue() as EditRouteLoaderData;
  const navigate = useNavigate();
  const submit = useSubmit();
  const [searchParams] = useSearchParams();

  const {
    routeId,
    editInitialValues,
    editOperatorsSeed,
    dataToCompare,
    hasSegments,
  } = loaderData;

  // returnTo por origen (adenda §1): la entrada (detalle/históricos) lo graba
  // como query param; el guardado con éxito vuelve ahí si es un path interno,
  // si no al detalle. La validación del path interno vive en la action (único
  // punto que hace el redirect), espejo de cómo el flujo de validación confía
  // en su `returnTo` de sesión pero aquí lo endurecemos con el prefijo `/`.
  const returnTo = searchParams.get('returnTo') ?? '';

  // Semilla del borrador de Operarios en sessionStorage — se escribe de forma
  // SÍNCRONA (antes de que se monte la tab Operaris, que lo rehidrata) para
  // que la card muestre la tripulación actual y `deriveCrew` la reproduzca en
  // el primer guardado aunque el usuario no toque nada. Se sobrescribe en cada
  // entrada (evita arrastre de un borrador de OTRO RV). Guardado con ref para
  // ejecutarse una sola vez por montaje.
  const seededRef = useRef(false);
  if (!seededRef.current && editOperatorsSeed) {
    seededRef.current = true;
    try {
      const draft = {
        operatorsTable: editOperatorsSeed.operatorsTable,
        vehiclesTable: [],
        vehicleTable: editOperatorsSeed.vehicleTable,
      };
      sessionStorage.setItem(
        EDIT_OPERATORS_KEY,
        compressSessionStorage(JSON.stringify(draft)),
      );
      // Buckets hermanos de un borrador de edición previo (defensa: que no
      // resuciten conductores/vehículos de otro RV).
      sessionStorage.removeItem(`${EDIT_OPERATORS_KEY}_drivers`);
      sessionStorage.removeItem(`${EDIT_OPERATORS_KEY}_interno`);
      sessionStorage.removeItem(`${EDIT_OPERATORS_KEY}_externals`);
    } catch (e) {
      console.warn('[EditRoute] no se pudo sembrar el borrador de operarios:', e);
    }
  }

  useLayoutEffect(() => {
    if (!editInitialValues || !dataToCompare) {
      navigate('/surveillance-route');
    }
  }, [editInitialValues, dataToCompare, navigate]);

  // Entrar por URL (2026-09-23): el lápiz ya no se le enseña a una tablet de
  // la otra patrulla, pero la dirección del asistente sí se puede teclear o
  // venir de un enlace. Mientras no se sepa de quién es el recorrido no se
  // echa a nadie (el loader acaba de traerlo, así que es un instante); en
  // cuanto se sabe y no puede editarlo, vuelve al detalle.
  const { user } = useAuth();
  const ownership = useSurveillanceRouteOwnership(routeId);
  const forbidden =
    !!ownership &&
    !canEditSurveillanceRoute(
      user?.user?.rolActive,
      ownership.state,
      user?.user,
      ownership
    );
  useLayoutEffect(() => {
    if (forbidden) navigate(`/surveillance-route/${routeId}`, { replace: true });
  }, [forbidden, navigate, routeId]);

  if (!editInitialValues || !dataToCompare || forbidden) {
    return null;
  }

  // Guardado en edición: diff contra el snapshot + guard sin-cambios, luego
  // submit a la action. Relaciones (teamIds/userIDs/vehicleAssignments)
  // COMPLETAS si cambiaron (el backend hace `set`/replace — spec §2, bullet 4).
  const handleEditSubmit = (payload: EditRouteSubmitPayload) => {
    const pkInit = parsePk(payload.pkInit);
    const pkEnd = parsePk(payload.pkEnd);
    if (!pkInit || !pkEnd) {
      // Inalcanzable (yup ya validó formato/rango); si ocurre es inconsistencia
      // real → mismo canal global que el resto del módulo.
      showErrorToast(i18n.t('toasts:error.generic'));
      return;
    }

    const input: SaveSurveillanceRouteInput = { id: routeId };

    if (payload.name !== dataToCompare.name) input.name = payload.name;
    // D2 (2026-07-20): descripción diffeada por separado del nombre — el
    // backend YA no la deriva del `name` (D1), así que el update solo la
    // toca si el usuario realmente la cambió.
    if (payload.description !== dataToCompare.description) {
      input.description = payload.description;
    }
    if (payload.concessionId !== dataToCompare.concessionId) {
      input.concessionId = payload.concessionId;
    }
    // Multi-carretera (2026-08-15): diff de CONJUNTOS, no de un id. Se manda
    // la lista entera si cambió alguna — el backend reemplaza el juego, así que
    // mandar media lista sería quitarle carreteras al recorrido.
    const roadsChanged =
      payload.roadIds.length !== dataToCompare.roadIds.length ||
      payload.roadIds.some((id, i) => id !== dataToCompare.roadIds[i]);
    if (roadsChanged) input.roadIds = payload.roadIds;
    // Los rangos van pegados a las carreteras: si cambia el juego, se mandan
    // todos; si solo se editó un PK, también (compararlos uno a uno no ahorra
    // nada y sí puede dejar el recorrido con rangos a medias).
    const rangesChanged =
      roadsChanged ||
      payload.roadRanges.length !== dataToCompare.roadRanges.length ||
      payload.roadRanges.some((r, i) => {
        const previous = dataToCompare.roadRanges[i];
        return (
          !previous ||
          r.roadId !== previous.roadId ||
          r.pkInit !== previous.pkInit ||
          r.pkEnd !== previous.pkEnd
        );
      });
    if (rangesChanged && payload.roadIds.length > 1) {
      input.roadRanges = payload.roadRanges.map((r) => {
        const init = parsePk(r.pkInit);
        const end = parsePk(r.pkEnd);
        return {
          roadId: r.roadId,
          pkInitKm: init?.km ?? 0,
          pkInitMeter: init?.meter ?? 0,
          pkEndKm: end?.km ?? 0,
          pkEndMeter: end?.meter ?? 0,
        };
      });
    }
    if (pkInit.km !== dataToCompare.pkInitKm) input.pkInitKm = pkInit.km;
    if (pkInit.meter !== dataToCompare.pkInitMeter) input.pkInitMeter = pkInit.meter;
    if (pkEnd.km !== dataToCompare.pkEndKm) input.pkEndKm = pkEnd.km;
    if (pkEnd.meter !== dataToCompare.pkEndMeter) input.pkEndMeter = pkEnd.meter;

    // Equipo: obligatorio (el wizard bloquea el submit sin equipo), array
    // completo si cambió — el update backend lee `teamIds`.
    if (payload.teamId !== dataToCompare.teamId) {
      input.teamIds = payload.teamId ? [payload.teamId] : [];
    }

    // Operarios: set completo si cambió el conjunto (orden irrelevante).
    const currentUserIds = [...payload.crew.operatorIds].sort();
    if (
      JSON.stringify(currentUserIds) !== JSON.stringify(dataToCompare.userIDs)
    ) {
      input.userIDs = payload.crew.operatorIds;
    }

    // Vehículos: replace completo si cambiaron las parejas vehículo→conductor.
    const currentAssignments = [...payload.crew.assignments].sort((a, b) =>
      a.vehicleId.localeCompare(b.vehicleId),
    );
    if (
      JSON.stringify(currentAssignments) !==
      JSON.stringify(dataToCompare.vehicleAssignments)
    ) {
      input.vehicleAssignments = payload.crew.assignments;
    }

    // Guard sin-cambios (mecanismo EditCampaign): sin campos más allá del `id`
    // no hay nada que guardar → feedback y no se dispara la mutation.
    const changedKeys = Object.keys(input).filter((k) => k !== 'id');
    if (changedKeys.length === 0) {
      showErrorToast(i18n.t('pages:surveillance.editNoChanges'));
      return;
    }

    const formData = new FormData();
    formData.set('input', JSON.stringify(input));
    formData.set('returnTo', returnTo);
    submit(formData, { method: 'POST', action: '.' });
  };

  return (
    <NewRoutePage
      editMode
      editInitialValues={editInitialValues}
      editOperatorsSeed={editOperatorsSeed}
      operatorsFormKey={EDIT_OPERATORS_KEY}
      hasSegments={hasSegments}
      originalRoadIds={dataToCompare.roadIds}
      onEditSubmit={handleEditSubmit}
    />
  );
}
